import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Headline from "../Headline/component";
import CardImageWrap from "../CardImageWrap/component";
import { Label } from "../../../Label/component";
import LinkWrap from "../../../LinkWrap/component";
import classNames from "classnames";
import { createLinkProps } from "../../../../utils/dataTransform/utils";
import { normalizeClass } from "../../../../utils/path/library";
import RankIndicator from "./components/RankIndicator/component";
import ReaderCount, {
    SRSReaderCount,
} from "./components/ReaderCount/component";
import { SECTIONS } from "../../../../constants/sections";
import Metadata from "../Metadata/component";
import { DISPLAY_TYPES } from "../../../ContentList/constants";

/**
 *
 * This Card type is used in the Discover area of story pages and the swimlanes that appear there
 *
 */
const CardDiscovery = ({
    author = {
        name: "",
        image: "",
    },
    className = "",
    rank = 0,
    showImage = true,
    videoCardOverlayType,
    videoDuration,
    videoAirDate,
    videoStreamType,
    imageURL,
    focus,
    displayType,
    feature,
    variant,
    countSrs,
    count,
    flag,
    external,
    flagId,
    baseSection,
    subSection,
    title,
    headlineId,
    sourceId,
    url,
    updateTime,
    departments,
    hideAuthorInfo,
    metadataId,
    cardClickHandler,
}) => {
    const domNode = useRef(null);

    useEffect(() => {
        // assign focus based on props
        if (focus !== undefined) {
            domNode.current.focus();
        }
    }, []);

    const readerCount = (showTrendingCount) => {
        if (!showTrendingCount) {
            return null;
        }

        if (variant === "SRS") {
            return <SRSReaderCount count={countSrs} />;
        }
        return <ReaderCount count={count} />;
    };

    const imageProps = {
        url: imageURL,
    };

    const videoLinkImageOverlayProps = {
        videoCardOverlayType,
        videoDuration,
        videoAirDate,
        videoStreamType,
    };

    const image = showImage ? (
        <CardImageWrap {...{ imageProps, videoLinkImageOverlayProps }} />
    ) : null;

    const labelProps = {
        flag: flag,
        external: external,
        id: flagId,
    };
    const isTrending = !!rank;
    const showTrendingCount =
        SECTIONS[baseSection] &&
        SECTIONS[baseSection].showTrendingCount !== "undefined"
            ? SECTIONS[baseSection].showTrendingCount
            : false;

    const rankIndicator =
        isTrending && rank !== 0 ? <RankIndicator rank={rank} /> : null;

    const flagClassName = normalizeClass("flag", flag);
    const classes = classNames(
        "card",
        flagClassName,
        "cardDiscovery",
        className,
        { isTrending: isTrending }
    );

    const { authorName, authorImage } = {
        author: author.name,
        authorImage: author.image,
    };
    const metaProps = {
        baseSection: baseSection,
        subSection: subSection,
        authorName: authorName,
        authorImage: authorImage,
        updated: updateTime,
        showTimestamp: false,
        departments: departments,
        url: url,
        displayComments: false,
        hideAuthorInfo: hideAuthorInfo,
    };
    const metaData = !isTrending ? (
        <Metadata id={metadataId} {...metaProps} />
    ) : null;

    const renderContainer = (innerContent) => {
        const featureAttribution = { ...feature };

        if (displayType === DISPLAY_TYPES.TYPE_LIST_ORDERED) {
            return (
                <li className={classes} ref={domNode} {...featureAttribution}>
                    {innerContent}
                </li>
            );
        }

        return (
            <div
                className={classes}
                ref={domNode}
                {...featureAttribution}
                data-testid="card-discovery-div-container"
            >
                {innerContent}
            </div>
        );
    };

    const renderInnerContent = () => {
        return (
            <>
                <LinkWrap
                    className="mediaItem"
                    {...createLinkProps({
                        sourceId,
                        external,
                        url,
                        cardClickHandler,
                    })}
                    tabIndex="-1"
                    aria-hidden={true}
                >
                    {image}
                </LinkWrap>
                <div className="contentWrapper">
                    <div className="card-content">
                        {rankIndicator}
                        <LinkWrap
                            className="headlineLink"
                            {...createLinkProps({
                                sourceId,
                                external,
                                url,
                                cardClickHandler,
                            })}
                        >
                            <Label {...labelProps} />
                            <Headline id={headlineId} text={title} />
                        </LinkWrap>
                        {readerCount(showTrendingCount)}
                        {metaData}
                    </div>
                </div>
            </>
        );
    };
    return renderContainer(renderInnerContent());
};

CardDiscovery.propTypes = {
    // REQUIRED

    // Is the URL external (in which case we don't handle it)?
    external: PropTypes.bool.isRequired,
    // The headline to display.
    title: PropTypes.string.isRequired,
    // The URL that the card links to
    url: PropTypes.string.isRequired,

    // OPTIONAL
    // The image URL of the image
    imageURL: PropTypes.string,
    // Author name and image object
    author: PropTypes.object,
    className: PropTypes.string,
    // Display comments for this card
    displayComments: PropTypes.bool,
    count: PropTypes.number,
    rank: PropTypes.number,
    showImage: PropTypes.bool,
};

export default CardDiscovery;
